import { Models } from "appwrite";
import CreatorAvatar from "@/components/shared/CreatorAvatar";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useMemo } from "react";

import { PostStats } from "@/components/shared";
import SellerRatingBadge from "@/components/shared/SellerRatingBadge";
import { useUserContext } from "@/context/AuthContext";
import { useGetMultipleUsersByIds } from "@/lib/react-query/queries";

type GridPostListProps = {
  posts: Models.Document[];
  showUser?: boolean;
  showStats?: boolean;
};

const GridPostList = ({
  posts,
  showUser = true,
  showStats = true,
}: GridPostListProps) => {
  const { user } = useUserContext();
  
  const activePosts = useMemo(() => 
    posts.filter((post: Models.Document) => post.isActivated), 
    [posts]
  );

  // Extract unique creator IDs
  const creatorIds = useMemo(() => {
    const ids = activePosts.map(post => post.creatorId).filter(Boolean);
    return [...new Set(ids)]; // Remove duplicates
  }, [activePosts]);

  // Fetch all creators at once
  const { data: creators = [], isLoading: creatorsLoading } = useGetMultipleUsersByIds(creatorIds);

  // Create a map for quick creator lookup
  const creatorMap = useMemo(() => {
    const map = new Map();
    creators.forEach(creator => {
      map.set(creator.$id, creator);
    });
    return map;
  }, [creators]);

  if (activePosts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] text-center w-full">
        <div className="w-16 h-16 bg-dark-4 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-light-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        </div>
        <p className="text-light-3 text-lg font-medium">No posts available</p>
      </div>
    );
  }

  // Show loading state while creators are being fetched
  if (creatorsLoading) {
    return (
      <div className="grid-container">
        {activePosts.map((post, _index) => (
          <div key={post.$id} className="animate-pulse">
            <div className="min-w-80 h-80 bg-dark-4 rounded-2xl mb-3"></div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-dark-4 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-dark-4 rounded mb-1"></div>
                <div className="h-3 bg-dark-4 rounded w-2/3"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <ul className="grid-container">
      {activePosts.map((post, index) => {
        // Get creator data from the map
        const creator = creatorMap.get(post.creatorId);
        const creatorName = creator?.name || post.creatorName || 'Unknown User';
        const creatorImage = creator?.imageUrl || post.creatorImageUrl || "/assets/icons/profile-placeholder.svg";
        
        return (
          <motion.li 
            key={post.$id} 
            className="group relative min-w-80 h-80"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ 
              duration: 0.4, 
              delay: index * 0.1,
              ease: "easeOut"
            }}
            whileHover={{ 
              scale: 1.02,
              transition: { duration: 0.2 }
            }}
          >
            <Link
              to={`/posts/${post.$id}`}
              state={{
                creatorName,
                creatorImageUrl: creatorImage,
              }}
              className="grid-post_link">
              <motion.div 
                className="h-full w-full bg-gradient-to-br from-dark-2 to-dark-4 rounded-2xl border border-dark-3/50 overflow-hidden relative group-hover:border-primary-500/30 transition-all duration-300"
                whileHover={{ boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}
              >
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/20 to-purple-500/20" />
                  <div className="absolute top-0 left-0 w-32 h-32 bg-primary-500/10 rounded-full -translate-x-16 -translate-y-16" />
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-purple-500/10 rounded-full translate-x-12 translate-y-12" />
                </div>

                {/* Content */}
                <div className="relative h-full flex flex-col p-6 pb-24">
                  {/* Post Type Indicator */}
                  <div className="flex items-center gap-2 mb-4">
                    {post.isSellingPost && (
                      <span className="px-3 py-1 bg-gradient-to-r from-blue-500/20 to-blue-600/20 text-blue-400 text-xs font-semibold rounded-full border border-blue-500/30">
                        💼 Business
                      </span>
                    )}
                    {post.individual === 'skilled-employee' && (
                      <span className="px-3 py-1 bg-gradient-to-r from-green-500/20 to-green-600/20 text-green-400 text-xs font-semibold rounded-full border border-green-500/30">
                        🎯 Skilled
                      </span>
                    )}
                    {post.individual === 'unemployed' && (
                      <span className="px-3 py-1 bg-gradient-to-r from-orange-500/20 to-orange-600/20 text-orange-400 text-xs font-semibold rounded-full border border-orange-500/30">
                        🔍 Seeking
                      </span>
                    )}
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 mb-4">
                    <motion.p 
                      className="text-light-1 text-sm leading-relaxed mb-3 group-hover:text-light-1 transition-colors line-clamp-4"
                      initial={{ opacity: 0.8 }}
                      whileHover={{ opacity: 1 }}
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                        hyphens: 'auto'
                      }}
                    >
                      {post.caption?.length > 140 
                        ? `${post.caption.substring(0, 140)}...` 
                        : post.caption}
                    </motion.p>

                    {/* Tags */}
                    {post.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {post.tags.slice(0, 2).map((tag: string, tagIndex: number) => (
                          tag.trim() && (
                            <motion.span
                              key={`${tag}-${tagIndex}`}
                              className="text-primary-400 text-xs px-2 py-1 bg-primary-500/10 rounded-md border border-primary-500/20 hover:bg-primary-500/20 transition-colors truncate max-w-[80px]"
                              whileHover={{ scale: 1.05 }}
                              title={`#${tag.trim()}`}
                            >
                              #{tag.trim().length > 8 ? `${tag.trim().substring(0, 8)}...` : tag.trim()}
                            </motion.span>
                          )
                        ))}
                        {post.tags.length > 2 && (
                          <span className="text-light-3 text-xs px-2 py-1 bg-dark-4/50 rounded-md">
                            +{post.tags.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Training Status for Skilled Employees */}
                  {post.individual === 'skilled-employee' && (
                    <motion.div 
                      className="mb-4"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      {post.isTrainingAvailable ? (
                        <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/20">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="text-green-400 text-xs font-medium">Available for Training</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-rose-500/10 to-pink-500/10 rounded-lg border border-rose-500/20">
                          <div className="w-2 h-2 bg-rose-500 rounded-full"></div>
                          <span className="text-rose-400 text-xs font-medium">Not Available for Training</span>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Hover Effect Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
                  
                  {/* View Post Indicator */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                    <div className="w-8 h-8 bg-primary-500/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-primary-500/30">
                      <svg className="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* User Info and Stats - Inside card at bottom with stacked layout */}
                <motion.div 
                  className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-dark-3 to-transparent rounded-b-[24px] flex flex-col gap-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.2 }}
                >
                  {/* User Info */}
                  {showUser && (
                    <motion.div 
                      className="flex items-center gap-2"
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="relative">
                        <CreatorAvatar
                          src={creatorImage}
                          name={creatorName}
                          className="w-8 h-8 rounded-full border-2 border-dark-3 group-hover:border-primary-500/50 transition-colors object-cover"
                        />
                      </div>
                      <motion.div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                        <p
                          className="min-w-0 text-light-1 text-sm font-medium truncate group-hover:text-primary-400 transition-colors"
                          title={creatorName}
                        >
                          {creatorName.length > 20
                            ? `${creatorName.substring(0, 20)}...`
                            : creatorName}
                        </p>
                        {post.isSellingPost && <SellerRatingBadge creatorId={post.creatorId} />}
                      </motion.div>
                    </motion.div>
                  )}
                  
                  {/* Stats - Full width row */}
                  {showStats && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.1 + 0.3 }}
                    >
                      <PostStats 
                        post={post} 
                        userId={user.id} 
                        postCreatorId={post.creatorId}
                      />
                    </motion.div>
                  )}
                </motion.div>
              </motion.div>
            </Link>
          </motion.li>
        );
      })}
    </ul>
  );
};

export default GridPostList;
